// Local-mode mocks for the Applications product: AuthNull as the identity
// provider for a tenant's own apps. Every POST /api/v1/idp/* call (see
// src/service/idpService.js and authnull-service internal/idp/admin.go), plus
// the directory calls the screens read: /ad/GetAllDomains (with an Entra
// directory), /ad/GetAllAdUsers and /ad/GetAllGroupsByDomain.
//
// Stateful until reload. Seeded like dev: "IdP Test App" (single-page OIDC)
// and "AMSI ERP (MFA API test)", both assigned to one Entra user, and one Entra
// sign-in source on directory 27 "test1". Plus "HR portal" (SAML) with no one
// assigned, so the Applications dashboard shows its warning. The sign-in rows
// for these apps are in appLogsMocks.js (same ids).
//
// App policies (policy type "app": who may use an MFA API app, and whose phone
// gets the push) on /api/v1/policyService/ListPolicy, CreateJSONPolicy,
// UpdateJSONPolicy and deletePolicyJSON, stateful too. Seeded with two policies
// for the MFA API app, as on dev: satyam.entra named by its full UPN (the
// directory user's username is the UPN and its email is sathyam@authnull.com,
// so a search by email for "satyam" finds nothing, and a username-vs-email
// check would flag it), last changed by a console user (by id); and priya.k,
// created through the API (updatedBy all zeros). Directory users include
// satyam.gupta in AD directory 25.
//
// Substring match, first wins — so the longer of two overlapping paths comes
// first ("/apps/saveassignments" before "/apps/save").
//
// The mock adapter always answers 200, so the server's 400s (a duplicate SAML
// entity ID, a bad redirect URI…) can't be shown here: save keeps the data
// consistent instead (a duplicate entity ID is simply stored), and the form
// checks the same rules client-side first.

import { MOCK_AD_DOMAINS } from "./sshProxyMocks";
import { listedPolicyType, mergePolicyJson } from "./dbPolicyMocks";

const ISSUER = "http://localhost:3000/api/v1/idp";
// As internal/idp/idp.go EndpointsFor.
const ENDPOINTS = {
  issuer: ISSUER,
  discoveryUrl: `${ISSUER}/.well-known/openid-configuration`,
  jwksUrl: `${ISSUER}/oauth2/jwks`,
  authorizationEndpoint: `${ISSUER}/oauth2/authorize`,
  tokenEndpoint: `${ISSUER}/oauth2/token`,
  userinfoEndpoint: `${ISSUER}/oauth2/userinfo`,
  endSessionEndpoint: `${ISSUER}/oauth2/logout`,
  revocationEndpoint: `${ISSUER}/oauth2/revoke`,
  upstreamRedirectUri: `${ISSUER}/login/upstream/callback`,
  samlMetadataUrl: `${ISSUER}/saml/metadata`,
  samlEntityId: `${ISSUER}/saml/metadata`,
  samlSsoUrl: `${ISSUER}/saml/sso`,
};

// A throwaway self-signed certificate body, one line (the fetch mock
// JSON-encodes the answer, which would turn line breaks into "\n").
const MOCK_CERT =
  "MIIBszCCAVmgAwIBAgIUQmFzZTY0TW9ja0NlcnRpZmljYXRlMAoGCCqGSM49BAMCMC8xLTArBgNVBAMMJGF1dGhudWxsLWxvY2FsLW1vY2staWRwLXNpZ25pbmcta2V5MB4XDTI2MDEwMTAwMDAwMFoXDTM2MDEwMTAwMDAwMFowLzEtMCsGA1UEAwwkYXV0aG51bGwtbG9jYWwtbW9jay1pZHAtc2lnbmluZy1rZXkwWTATBgcqhkjOPQIBBggqhkjOPQMBBwNCAAQ=";

const ENTRA_DOMAIN = {
  id: 27,
  directoryName: "test1",
  domainName: "satyamgupta1807outlook.onmicrosoft.com",
  identity_source: "EntraID",
  integration_type: "Agentless",
  status: "Active",
  userCount: 3,
  groupCount: 2,
};

// An AD directory, for app policies (a directory user can be AD or Entra).
const AD_DOMAIN = {
  id: 25,
  directoryName: "corp",
  domainName: "corp.authnull.lab",
  identity_source: "AD",
  integration_type: "Agent",
  status: "Registered",
  userCount: 1,
  groupCount: 0,
};

const DIRECTORY_USERS = [
  { id: 18578, emailId: "sathyam@authnull.com", user_principal_name: "satyam.entra@satyamgupta1807outlook.onmicrosoft.com", display_name: "Satyam (Entra)", username: "satyam.entra@satyamgupta1807outlook.onmicrosoft.com", domainId: 27, activeDirectoryName: "test1", status: "Active", userType: "NormalUser" },
  { id: 18579, emailId: "priya.k@satyamgupta1807outlook.onmicrosoft.com", user_principal_name: "priya.k@satyamgupta1807outlook.onmicrosoft.com", display_name: "Priya K", username: "priya.k", domainId: 27, activeDirectoryName: "test1", status: "Active", userType: "NormalUser" },
  { id: 18580, emailId: "ops.bot@satyamgupta1807outlook.onmicrosoft.com", user_principal_name: "ops.bot@satyamgupta1807outlook.onmicrosoft.com", display_name: "Ops Bot", username: "ops.bot", domainId: 27, activeDirectoryName: "test1", status: "Active", userType: "ServiceAccount" },
  { id: 18601, emailId: "satyam.gupta@corp.authnull.lab", user_principal_name: "satyam.gupta@corp.authnull.lab", display_name: "Satyam Gupta", username: "satyam.gupta", logoname: "CORP\\satyam.gupta", domainId: 25, activeDirectoryName: "corp", status: "Active", userType: "NormalUser" },
];

const DIRECTORY_GROUPS = [
  { id: 501, groupName: "ERP-Users", adId: 27 },
  { id: 502, groupName: "VPN-Users", adId: 27 },
];

const now = () => new Date().toISOString();
const uuid = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
const newSecret = () => {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
  let s = "ancs_";
  for (let i = 0; i < 43; i += 1) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  return s;
};
// What the console may show of a secret (apps.go secretHint): "ancs_" and four
// more characters, with when it was made.
const secretFields = (secret) => ({ secretHint: secret.slice(0, 9), secretCreatedAt: now() });

const apps = [
  {
    id: "82fb7e5f-1c4d-4f7a-9d3e-5b2a6c8e0f11",
    tenantId: 1,
    name: "IdP Test App",
    protocol: "oidc",
    enabled: true,
    logoUrl: "",
    mfaMode: "always",
    oidc: {
      clientId: "c2fb8383-6a1e-4b0f-8d2c-3e4f5a6b7c8d",
      tokenEndpointAuthMethod: "none",
      redirectUris: ["http://localhost:8765/callback"],
      postLogoutRedirectUris: [],
      allowedScopes: ["openid", "profile", "email"],
      requirePkce: true,
      hasSecret: false,
    },
    createdAt: "2026-10-20T09:12:00Z",
    updatedAt: "2026-10-20T09:12:00Z",
  },
  {
    id: "4a7c2e90-8b3d-4e1f-a6c5-0d9e8f7a6b5c",
    tenantId: 1,
    name: "AMSI ERP (MFA API test)",
    protocol: "mfa_api",
    enabled: true,
    logoUrl: "",
    mfaMode: "always",
    // The redirect flow (apps.go): callback, session length, methods.
    sessionTtlSeconds: 28800,
    mfaMethods: ["push", "totp"],
    oidc: {
      clientId: "9e1d4c7b-2a5f-4d8e-b3c6-7f0a1e2d3c4b",
      tokenEndpointAuthMethod: "client_secret_basic",
      redirectUris: ["https://erp.amsi.com/authnull/callback", "http://localhost:8080/authnull/callback"],
      postLogoutRedirectUris: [],
      allowedScopes: ["mfa"],
      requirePkce: false,
      hasSecret: true,
      secretHint: "ancs_Wad4",
      secretCreatedAt: "2026-10-21T14:40:00Z",
    },
    createdAt: "2026-10-21T14:40:00Z",
    updatedAt: "2026-10-21T14:40:00Z",
  },
  {
    id: "c3d9a1e7-5f2b-4c8d-9e0a-1b2c3d4e5f60",
    tenantId: 1,
    name: "HR portal",
    protocol: "saml",
    enabled: true,
    logoUrl: "",
    mfaMode: "always",
    saml: {
      entityId: "https://hr.example.com/saml/metadata",
      acsUrls: ["https://hr.example.com/saml/acs"],
      nameIdFormat: "emailAddress",
      nameIdSource: "email",
      attributes: { email: "email", name: "name", username: "username", groups: "groups" },
    },
    createdAt: "2026-10-22T11:05:00Z",
    updatedAt: "2026-10-22T11:05:00Z",
  },
];

const SATYAM = { type: "user", principal: "18578", label: "satyam.entra@satyamgupta1807outlook.onmicrosoft.com" };
// appId → [{ type, principal }]
const assignments = {
  [apps[0].id]: [{ ...SATYAM }],
  [apps[1].id]: [{ ...SATYAM }],
};

const loginSources = [
  {
    id: "5d2e8f1a-3b4c-4d5e-9f6a-7b8c9d0e1f2a",
    tenantId: 1,
    kind: "entra",
    identitySourceId: 27,
    identitySourceName: "test1",
    priority: 100,
    domainSuffixes: [],
    enabled: true,
    createdAt: "2026-10-20T09:00:00Z",
    updatedAt: "2026-10-20T09:00:00Z",
  },
];

const KNOWN_SCOPES = ["openid", "profile", "email", "groups"];
const scopesOf = (list) => {
  const want = new Set(["openid", ...(list && list.length ? list : ["profile", "email"])]);
  return KNOWN_SCOPES.filter((s) => want.has(s));
};
const cleanList = (list) => [...new Set((list || []).map((s) => String(s).trim()).filter(Boolean))];

// Entity ID and HTTP-POST ACS URLs out of pasted SP metadata, as the server does.
const fromMetadata = (xml = "") => {
  const entity = (xml.match(/entityID="([^"]+)"/) || [])[1] || "";
  const acs = [];
  const re = /<(?:\w+:)?AssertionConsumerService\b([^>]*)>/g;
  let m;
  while ((m = re.exec(xml))) {
    const attrs = m[1];
    if (/Binding="[^"]*HTTP-POST"/.test(attrs)) {
      const loc = (attrs.match(/Location="([^"]+)"/) || [])[1];
      if (loc) acs.push(loc);
    }
  }
  return { entity, acs };
};

const labelOf = (a) => {
  if (a.type !== "user") return undefined;
  const u = DIRECTORY_USERS.find((x) => String(x.id) === String(a.principal));
  return u ? u.user_principal_name : "";
};

const assignmentsOf = (id) =>
  (assignments[id] || [])
    .map((a) => ({ type: a.type, principal: a.principal, ...(a.type === "user" ? { label: labelOf(a) } : {}) }))
    .sort((x, y) => x.type.localeCompare(y.type) || x.principal.toLowerCase().localeCompare(y.principal.toLowerCase()));

const saveAppMock = (body = {}) => {
  const protocol = body.protocol || "oidc";
  const existing = body.id ? apps.find((a) => a.id === body.id) : null;
  const app = existing
    ? { ...existing }
    : { id: uuid(), tenantId: Number(body.tenantId) || 1, protocol, mfaMode: "always", createdAt: now() };
  Object.assign(app, {
    name: String(body.name || "").trim(),
    logoUrl: String(body.logoUrl || "").trim(),
    enabled: !body.disabled,
    updatedAt: now(),
  });
  let clientSecret;
  if (protocol === "saml") {
    const s = body.saml || {};
    const meta = s.metadata ? fromMetadata(s.metadata) : { entity: "", acs: [] };
    const acs = cleanList(s.acsUrls);
    app.saml = {
      entityId: String(s.entityId || "").trim() || meta.entity,
      acsUrls: acs.length ? acs : meta.acs,
      nameIdFormat: s.nameIdFormat || "emailAddress",
      nameIdSource: s.nameIdSource || "email",
      attributes: s.attributes
        ? Object.fromEntries(Object.entries(s.attributes).map(([k, v]) => [k, String(v || "").trim()]).filter(([, v]) => v))
        : { email: "email", name: "name", username: "username", groups: "groups" },
    };
    delete app.oidc;
  } else if (protocol === "mfa_api") {
    const prev = existing?.oidc;
    if (!existing) clientSecret = newSecret();
    // As apps.go normaliseAPIApp / keepAPISettings: 0 and [] keep the stored
    // values (defaults 8 hours, push + totp); the redirect URIs are kept only
    // when the save carries none of the three settings.
    const ttl = Number(body.sessionTtlSeconds) || 0;
    const methods = (body.mfaMethods || []).map((m) => String(m).trim().toLowerCase()).filter((m) => m === "push" || m === "totp");
    const redirects = cleanList(body.oidc?.redirectUris);
    const keepRedirects = !redirects.length && !ttl && !methods.length;
    app.sessionTtlSeconds = ttl || existing?.sessionTtlSeconds || 28800;
    app.mfaMethods = methods.length ? [...new Set(methods)] : existing?.mfaMethods || ["push", "totp"];
    app.oidc = {
      clientId: prev?.clientId || uuid(),
      tokenEndpointAuthMethod: body.oidc?.tokenEndpointAuthMethod || "client_secret_basic",
      redirectUris: keepRedirects ? prev?.redirectUris || [] : redirects,
      postLogoutRedirectUris: [],
      allowedScopes: ["mfa"],
      requirePkce: false,
      hasSecret: true,
      ...(clientSecret
        ? secretFields(clientSecret)
        : { secretHint: prev?.secretHint || "", secretCreatedAt: prev?.secretCreatedAt || now() }),
    };
  } else {
    const o = body.oidc || {};
    const method = o.tokenEndpointAuthMethod || "client_secret_basic";
    const confidential = method !== "none";
    const hadSecret = !!existing?.oidc?.hasSecret;
    // A secret is made on create, and when a public client becomes confidential.
    if (confidential && !hadSecret) clientSecret = newSecret();
    let secretInfo = {};
    if (clientSecret) secretInfo = secretFields(clientSecret);
    else if (confidential) secretInfo = { secretHint: existing.oidc.secretHint || "", secretCreatedAt: existing.oidc.secretCreatedAt || now() };
    app.oidc = {
      clientId: existing?.oidc?.clientId || uuid(),
      tokenEndpointAuthMethod: method,
      redirectUris: cleanList(o.redirectUris),
      postLogoutRedirectUris: cleanList(o.postLogoutRedirectUris),
      allowedScopes: scopesOf(o.allowedScopes),
      requirePkce: o.requirePkce === undefined ? true : !!o.requirePkce,
      hasSecret: confidential,
      ...secretInfo,
    };
  }
  if (existing) apps[apps.indexOf(existing)] = app;
  else apps.push(app);
  return clientSecret ? { app, clientSecret } : { app };
};

const saveSourceMock = (body = {}) => {
  const existing = body.id ? loginSources.find((s) => s.id === body.id) : null;
  const dir = Number(body.identitySourceId) === ENTRA_DOMAIN.id ? ENTRA_DOMAIN : null;
  const src = {
    ...(existing || { id: uuid(), createdAt: now(), priority: 100 }),
    tenantId: Number(body.tenantId) || 1,
    kind: body.kind || "entra",
    identitySourceId: Number(body.identitySourceId) || 0,
    identitySourceName: dir ? dir.directoryName : "",
    domainSuffixes: cleanList((body.domainSuffixes || []).map((d) => String(d).toLowerCase().replace(/^@/, ""))),
    enabled: !body.disabled,
    updatedAt: now(),
  };
  if (existing) loginSources[loginSources.indexOf(existing)] = src;
  else loginSources.push(src);
  return { loginSource: src };
};

/* ------------------------------------------------------- app policies */

// The mock MFA API app (apps[1]).
const policies = [
  {
    id: "6b0e3f2a-9c1d-4e5f-8a7b-1c2d3e4f5a6b",
    policyName: "AMSI ERP – Satyam",
    policyType: "app",
    status: "Approved",
    version: 1,
    generatedBy: "",
    policyInvokationCount: 0,
    createdAt: "2026-10-23T10:15:00Z",
    updatedAt: "2026-10-23T10:15:00Z",
    // CreateJSONPolicy stores a random UUID as the creator; updatedBy is the
    // console user's id (listPlatformUsers userId 3, sumit@authnull.com).
    createdBy: "f00a2ebe-3c1d-4b7e-9a2f-6d5e4c3b2a10",
    updatedBy: "3",
    policyJson: {
      policyName: "AMSI ERP – Satyam",
      policyType: "app",
      policyFlow: "mfa_required",
      app: { apps: ["4a7c2e90-8b3d-4e1f-a6c5-0d9e8f7a6b5c"], users: ["satyam.entra@satyamgupta1807outlook.onmicrosoft.com"], directoryId: 27 },
      permissions: { iam_users: ["guptasatyam1807@gmail.com"] },
      // Conditional access (authnull-service #144): only applies in office hours.
      conditions: { time: { timezone: "Asia/Kolkata", days: ["mon", "tue", "wed", "thu", "fri"], start: "09:00", end: "18:00" } },
    },
  },
  {
    id: "9d4c1b7e-2f3a-4c5d-8e6f-0a1b2c3d4e5f",
    policyName: "AMSI ERP – Priya",
    policyType: "app",
    status: "Approved",
    version: 1,
    generatedBy: "",
    policyInvokationCount: 0,
    createdAt: "2026-10-24T08:30:00Z",
    updatedAt: "2026-10-24T08:30:00Z",
    // Created through the API: no console user.
    createdBy: "00000000-0000-0000-0000-000000000000",
    updatedBy: "00000000-0000-0000-0000-000000000000",
    policyJson: {
      policyName: "AMSI ERP – Priya",
      policyType: "app",
      policyFlow: "allow",
      app: { apps: ["4a7c2e90-8b3d-4e1f-a6c5-0d9e8f7a6b5c"], users: ["priya.k"], directoryId: 27 },
      permissions: { iam_users: ["sumit@authnull.com"] },
    },
  },
];

// AD and RADIUS policies share the policy service. Kept in the same store so
// list, create and update round-trip in local mode (conditions, actions and
// fallback survive an edit, or don't).
policies.push(
  {
    id: "ad000000-0000-4000-8000-000000000001",
    policyName: "Domain admins — MFA out of hours",
    policyType: "AD",
    status: "Approved",
    version: 1,
    createdAt: "2026-10-20T09:00:00Z",
    updatedAt: "2026-10-28T09:00:00Z",
    policyJson: {
      policyType: "AD",
      policyKind: "static",
      authTypes: ["ad"],
      policyFlow: "mfa_required",
      mfaPromptTemplate: "",
      ad: { adId: 25, domain: "corp.authnull.lab", groups: ["Domain Admins"], users: [], ous: [], matchAll: false, sources: [], destinations: [] },
      protocolControl: { allowed: [] },
      conditions: {
        identity: { excludeUsers: ["breakglass"] },
        time: { timezone: "Asia/Kolkata", days: ["mon", "tue", "wed", "thu", "fri"], start: "09:00", end: "18:00", outside: true },
        network: { include: ["10.0.0.0/8"] },
      },
      actions: { approval: { required: true, approverGroups: ["secops"], requireJustification: true, validMinutes: 60 } },
    },
  },
  {
    id: "ad000000-0000-4000-8000-000000000002",
    policyName: "Everyone — deny anything else",
    policyType: "AD",
    status: "Approved",
    version: 1,
    createdAt: "2026-10-21T09:00:00Z",
    updatedAt: "2026-10-21T09:00:00Z",
    policyJson: {
      policyType: "AD",
      policyKind: "static",
      authTypes: ["ad"],
      policyFlow: "block",
      ad: { adId: 25, domain: "corp.authnull.lab", groups: [], users: [], ous: [], matchAll: true, sources: [], destinations: [] },
      protocolControl: { allowed: [] },
      fallback: true,
    },
  },
  // Spec 7.3: a policy from before Source moved to the Network condition.
  // The editor shows ad.sources as Network "Only from" and clears them on save.
  {
    id: "ad000000-0000-4000-8000-000000000003",
    policyName: "Helpdesk — file servers from the office",
    policyType: "AD",
    status: "Approved",
    version: 3,
    createdAt: "2026-09-02T09:00:00Z",
    updatedAt: "2026-10-12T09:00:00Z",
    policyJson: {
      policyType: "AD",
      policyKind: "static",
      authTypes: ["ad"],
      policyFlow: "allow",
      ad: { adId: 25, domain: "corp.authnull.lab", groups: ["Helpdesk"], users: ["jdoe"], ous: [], matchAll: false, sources: ["192.168.10.0/24", "10.20.0.5"], destinations: ["cifs/fs01.corp.authnull.lab"] },
      protocolControl: { allowed: ["smb", "kerberos"] },
    },
  },
  // Spec 7.4: a condition the console no longer offers (device), set
  // outside it. Shown read-only; kept on save unless removed. Also a key no
  // console knows (riskEngine), which every save must keep.
  {
    id: "ad000000-0000-4000-8000-000000000004",
    policyName: "Finance — deny unmanaged devices",
    policyType: "AD",
    status: "Approved",
    version: 1,
    createdAt: "2026-10-25T09:00:00Z",
    updatedAt: "2026-10-25T09:00:00Z",
    policyJson: {
      policyType: "AD",
      policyKind: "static",
      authTypes: ["ad"],
      policyFlow: "block",
      ad: { adId: 25, domain: "corp.authnull.lab", groups: ["Finance"], users: [], ous: [], matchAll: false, sources: [], destinations: [] },
      protocolControl: { allowed: ["ldap"] },
      conditions: { device: { trust: ["unmanaged"] }, privilege: { levels: ["admin"] } },
      riskEngine: { model: "v2" },
    },
  },
  {
    id: "ad000000-0000-4000-8000-000000000005",
    policyName: "Contractors — LDAP only (paused)",
    policyType: "AD",
    status: "Suspended",
    version: 1,
    createdAt: "2026-10-26T09:00:00Z",
    updatedAt: "2026-10-26T09:00:00Z",
    policyJson: {
      policyType: "AD",
      policyKind: "static",
      authTypes: ["ad"],
      policyFlow: "mfa_required",
      mfaPromptTemplate: "$user is signing in to $destination",
      ad: { adId: 25, domain: "corp.authnull.lab", groups: [], users: ["asmith", "contractor01"], ous: ["OU=Contractors,DC=corp,DC=authnull,DC=lab"], matchAll: false, sources: [], destinations: [] },
      protocolControl: { allowed: ["ldap"] },
    },
  },
  {
    id: "4ad10000-0000-4000-8000-000000000001",
    policyName: "VPN — office networks",
    policyType: "radius",
    status: "Approved",
    version: 1,
    createdAt: "2026-10-22T09:00:00Z",
    updatedAt: "2026-10-22T09:00:00Z",
    policyJson: {
      policyName: "VPN — office networks",
      policyType: "radius",
      policyFlow: "mfa_required",
      radius: { domain: "corp.authnull.lab", users: [], groups: ["VPN Users"], ous: [], matchAll: false, clients: [], callingStations: [], serviceTypes: [] },
      conditions: { network: { include: ["192.168.0.0/16"], exclude: ["192.168.99.0/24"] } },
    },
  },
  // Paused, so an edit shows the "keep it off" step: the update below saves a
  // RADIUS edit as a new active version, as the service does, and the editor
  // switches it off again.
  {
    id: "4ad10000-0000-4000-8000-000000000002",
    policyName: "Device admin logins from the lab",
    policyType: "radius",
    status: "Suspended",
    version: 1,
    createdAt: "2026-10-23T09:00:00Z",
    updatedAt: "2026-10-23T09:00:00Z",
    policyJson: {
      policyName: "Device admin logins from the lab",
      policyType: "radius",
      policyFlow: "block",
      radius: {
        domain: "corp.authnull.lab",
        users: ["asmith"],
        groups: ["Helpdesk"],
        ous: [],
        matchAll: false,
        clients: ["vpn-edge-01", "10.40.0.0/24"],
        callingStations: ["10.99.0.0/16"],
        serviceTypes: ["administrative", "nas-prompt"],
      },
      conditions: { identity: { excludeUsers: ["breakglass"] } },
    },
  },
);

const isAppPolicy = (body) =>
  String(body?.policyType || body?.policyJson?.policyType || "").toLowerCase() === "app";
// ListPolicy filters on filters[{ filterParameter: "policyType" }] only; a
// top-level policyType is ignored, as on the backend.
const asksForAppPolicies = (body) => listedPolicyType(body) === "app";
// What the policy-service calls answer when they aren't about app policies:
// the generic "/policy" entry in mockData.js, which they matched before.
const NOT_APP = () => ({ code: 200, data: [], total: 0 });
const typeOf = (p) => String(p?.policyType || p?.policyJson?.policyType || "").toLowerCase();
// The AD and RADIUS policies, which ask for their type in the body.
const isStoredType = (body) => ["ad", "radius"].includes(typeOf(body));

// As the policy service compares users: no DOMAIN\ prefix or @suffix, any case.
const principalOf = (u) =>
  String(u || "")
    .trim()
    .replace(/^.*\\/, "")
    .replace(/^([^@]+)@.*$/, "$1")
    .toLowerCase();

const samePrincipal = (a, b) => {
  const x = a.policyJson?.app || {};
  const y = b?.app || {};
  return (
    (x.apps || [])[0] === (y.apps || [])[0] &&
    principalOf((x.users || [])[0]) === principalOf((y.users || [])[0]) &&
    (Number(x.directoryId) || 0) === (Number(y.directoryId) || 0)
  );
};

const appPolicyMocks = [
  {
    pattern: "/policyservice/listpolicy",
    respond: (body = {}) => {
      if (!asksForAppPolicies(body)) {
        const asked = listedPolicyType(body);
        if (!["ad", "radius"].includes(asked)) return NOT_APP();
        const rows = policies.filter((p) => typeOf(p) === asked).map((p) => ({ ...p }));
        return { code: 200, status: "success", data: rows, policies: rows, total: rows.length, total_pages: 1 };
      }
      const limit = Number(body.limit) || 50;
      const page = Math.max(1, Number(body.page) || 1);
      const list = policies.filter((p) => typeOf(p) === "app").sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
      return {
        code: 200,
        status: "success",
        message: "ok",
        data: list.slice((page - 1) * limit, page * limit).map((p) => ({ ...p })),
        limit,
        page,
        total_pages: Math.max(1, Math.ceil(list.length / limit)),
      };
    },
  },
  {
    // The mock adapter always answers 200, so a conflict comes back as a body
    // with code 409, which createAppPolicy treats the same as an HTTP 409.
    pattern: "/policyservice/createjsonpolicy",
    respond: (body = {}) => {
      if (!isAppPolicy(body)) {
        if (!isStoredType(body)) return NOT_APP();
        const id = uuid();
        policies.push({
          id,
          policyName: body.policyName || body.policyJson?.policyName || "",
          policyType: body.policyType,
          status: "Approved",
          version: 1,
          createdAt: now(),
          updatedAt: now(),
          policyJson: { ...body.policyJson },
        });
        return { code: 200, status: "success", message: "Successfully created policy", data: id };
      }
      const clash = policies.filter((p) => samePrincipal(p, body.policyJson));
      if (clash.length && !body.force) {
        return {
          code: 409,
          status: "conflict",
          message: "Conflicting policies found",
          data: "00000000-0000-0000-0000-000000000000",
          conflicts: clash.map((p) => ({ ...p })),
          warning: "",
        };
      }
      const id = uuid();
      policies.push({
        id,
        policyName: body.policyName || body.policyJson?.policyName || "",
        policyType: "app",
        status: "Approved",
        version: 1,
        generatedBy: "",
        policyInvokationCount: 0,
        createdAt: now(),
        updatedAt: now(),
        createdBy: body.createdBy || "localdev@authnull.com",
        updatedBy: body.createdBy || "localdev@authnull.com",
        policyJson: { ...body.policyJson, policyType: "app" },
      });
      return {
        code: 200,
        status: "success",
        message: "Successfully created policy",
        data: id,
        ...(clash.length ? { conflicts: clash.map((p) => ({ ...p })), warning: "Created with conflicts overridden — review with admin" } : {}),
      };
    },
  },
  {
    // Editing makes a new version: a new id, and the old one is no longer listed.
    pattern: "/policyservice/updatejsonpolicy",
    respond: (body = {}) => {
      const i = policies.findIndex((p) => p.id === body.id);
      if (i < 0) return isAppPolicy(body) ? { updatedPolicyID: { code: 404, status: "failed", message: "Policy Does Not Exist", data: "" } } : NOT_APP();
      const old = policies[i];
      const next = {
        ...old,
        // The service saves a RADIUS edit as a new, active version.
        status: typeOf(old) === "radius" ? "Approved" : old.status,
        id: uuid(),
        version: (Number(old.version) || 1) + 1,
        policyName: body.policyName || old.policyName,
        // The server merges policyJson onto the stored policy (policy-save-merge).
        policyJson: { ...mergePolicyJson(old.policyJson, body.policyJson), policyType: old.policyJson?.policyType || old.policyType },
        updatedAt: now(),
        updatedBy: body.updatedBy || "localdev@authnull.com",
      };
      policies.splice(i, 1, next);
      return { updatedPolicyID: { code: 200, status: "success", message: "Policy updated", data: next.id } };
    },
  },
  {
    pattern: "/policyservice/deletepolicyjson",
    respond: (body = {}) => {
      const i = policies.findIndex((p) => p.id === body.policyId);
      if (i < 0) return NOT_APP();
      policies.splice(i, 1);
      return { code: 200, status: "success", message: "Policy deleted" };
    },
  },
];

// AD lookups for the policy editor (directory 25 "corp"): users, groups, OUs,
// the impact preview, the dry run and suspend/resume.
const AD_LOOKUP_USERS = [
  { id: 1, username: "satyam.gupta", logoname: "CORP\\satyam.gupta", email: "satyam.gupta@corp.authnull.lab" },
  { id: 2, username: "jdoe", logoname: "CORP\\jdoe", email: "jdoe@corp.authnull.lab" },
  { id: 3, username: "asmith", logoname: "CORP\\asmith", email: "asmith@corp.authnull.lab" },
  { id: 4, username: "breakglass", logoname: "CORP\\breakglass", email: "" },
  { id: 5, username: "contractor01", logoname: "CORP\\contractor01", email: "" },
];
const AD_LOOKUP_GROUPS = [
  { id: 11, groupName: "Domain Admins", memberCount: 3 },
  { id: 12, groupName: "Helpdesk", memberCount: 6 },
  { id: 13, groupName: "Finance", memberCount: 12 },
  { id: 14, groupName: "VPN Users", memberCount: 40 },
];
const AD_LOOKUP_OUS = [
  { ou_name: "OU=Servers,DC=corp,DC=authnull,DC=lab", ad_id: 25 },
  { ou_name: "OU=Contractors,DC=corp,DC=authnull,DC=lab", ad_id: 25 },
  { ou_name: "OU=Workstations,DC=test01,DC=local", ad_id: 1 },
];
const byFilter = (list, field, q) => list.filter((x) => String(x[field]).toLowerCase().includes(String(q || "").toLowerCase()));
const AD_POLICY_MOCKS = [
  {
    pattern: "/policy/listadusers",
    respond: (body = {}) => {
      const data = Number(body.adId) === 25 ? byFilter(AD_LOOKUP_USERS, "username", body.filter) : [];
      return { code: 200, status: "success", data, total: data.length };
    },
  },
  {
    pattern: "/policy/listadgroups",
    respond: (body = {}) => {
      const data = Number(body.adId) === 25 ? byFilter(AD_LOOKUP_GROUPS, "groupName", body.filter) : [];
      return { code: 200, status: "success", data, total: data.length };
    },
  },
  { pattern: "/policyservice/listou", respond: () => ({ code: 200, status: "success", data: AD_LOOKUP_OUS.map((o) => ({ ...o })) }) },
  {
    pattern: "/policy/json/previewimpact",
    respond: (body = {}) => {
      const groups = (body.adGroups || []).reduce((n, g) => n + (AD_LOOKUP_GROUPS.find((x) => x.groupName === g)?.memberCount || 0), 0);
      return { matchedUsers: groups + (body.adUsers || []).length + (body.adOus || []).length * 4 };
    },
  },
  {
    pattern: "/policy/json/geteffectivepolicy",
    respond: (body = {}) => {
      const user = String(body.ad_user || "").toLowerCase();
      const hit = policies.find(
        (p) => typeOf(p) === "ad" && p.status === "Approved" && !p.policyJson?.fallback && (p.policyJson?.ad?.users || []).map((u) => u.toLowerCase()).includes(user),
      );
      if (!hit) return { action: "block", mfa_required: false, match_reason: "No policy names this user; the fallback policy denies." };
      return { action: hit.policyJson.policyFlow, mfa_required: hit.policyJson.policyFlow === "mfa_required", match_reason: `Matched \u201c${hit.policyName}\u201d.` };
    },
  },
  {
    // Suspend / resume: { action: { actionType: "status", actionIds, actionValue } }.
    pattern: "/policyservice/policyaction",
    respond: (body = {}) => {
      const a = body.action || {};
      if (a.actionType === "status") {
        policies.forEach((p) => {
          if ((a.actionIds || []).includes(p.id)) p.status = a.actionValue;
        });
      }
      return { code: 200, status: "success", message: "Policy updated" };
    },
  },
];

const removeWhere = (list, pred) => {
  const i = list.findIndex(pred);
  if (i >= 0) list.splice(i, 1);
};

export const IDP_MOCKS = [
  ...appPolicyMocks,
  ...AD_POLICY_MOCKS,
  { pattern: "/idp/apps/list", respond: () => ({ apps: apps.map((a) => ({ ...a })) }) },
  // Before "/idp/apps/save", which is a substring of it.
  {
    pattern: "/idp/apps/saveassignments",
    respond: (body = {}) => {
      assignments[body.id] = (body.assignments || []).slice(0, 500).map((a) => ({ type: a.type, principal: String(a.principal) }));
      return { assignments: assignmentsOf(body.id) };
    },
  },
  { pattern: "/idp/apps/save", respond: (body) => saveAppMock(body || {}) },
  {
    pattern: "/idp/apps/delete",
    respond: (body = {}) => {
      removeWhere(apps, (a) => a.id === body.id);
      delete assignments[body.id];
      return { status: "deleted" };
    },
  },
  {
    pattern: "/idp/apps/rotatesecret",
    respond: (body = {}) => {
      // Like the server, this does not change the app's updatedAt.
      const app = apps.find((a) => a.id === body.id);
      const clientSecret = newSecret();
      if (app?.oidc) app.oidc = { ...app.oidc, hasSecret: true, ...secretFields(clientSecret) };
      return { clientSecret };
    },
  },
  { pattern: "/idp/apps/assignments", respond: (body = {}) => ({ assignments: assignmentsOf(body.id) }) },
  { pattern: "/idp/loginsources/list", respond: () => ({ loginSources: loginSources.map((s) => ({ ...s })) }) },
  { pattern: "/idp/loginsources/save", respond: (body) => saveSourceMock(body || {}) },
  {
    pattern: "/idp/loginsources/delete",
    respond: (body = {}) => {
      removeWhere(loginSources, (s) => s.id === body.id);
      return { status: "deleted" };
    },
  },
  { pattern: "/idp/settings", respond: () => ({ endpoints: { ...ENDPOINTS } }) },
  // Public: fetched by "Download certificate". The fetch mock JSON-encodes
  // this string; the screen reads the certificate out of the text either way.
  {
    pattern: "/idp/saml/metadata",
    respond: () =>
      `<md:EntityDescriptor xmlns:md="urn:oasis:names:tc:SAML:2.0:metadata" xmlns:ds="http://www.w3.org/2000/09/xmldsig#" entityID="${ENDPOINTS.samlEntityId}"><md:IDPSSODescriptor><md:KeyDescriptor use="signing"><ds:KeyInfo><ds:X509Data><ds:X509Certificate>${MOCK_CERT}</ds:X509Certificate></ds:X509Data></ds:KeyInfo></md:KeyDescriptor></md:IDPSSODescriptor></md:EntityDescriptor>`,
  },

  // Directory calls. Ahead of sshProxyMocks' static "/ad/getalldomains":
  // the Entra directory is listed only when Entra directories are asked for,
  // so the other screens keep the list they had.
  {
    pattern: "/ad/getalldomains",
    respond: (body = {}) => {
      const source = body?.filters?.identitySource;
      // Unfiltered: every directory, AD and Entra (the app-policy screens ask
      // for all of them).
      const domains = source === "EntraID" ? [ENTRA_DOMAIN] : source ? [] : [...MOCK_AD_DOMAINS, AD_DOMAIN, ENTRA_DOMAIN];
      return { code: 200, status: "success", domains, totalCount: domains.length };
    },
  },
  {
    pattern: "/ad/getalladusers",
    respond: (body = {}) => {
      const f = body?.filters || {};
      const q = String(f.emailId || "").toLowerCase();
      const dir = String(f.directoryName || "").toLowerCase();
      const users = DIRECTORY_USERS.filter(
        (u) => (!q || u.emailId.toLowerCase().includes(q)) && (!dir || u.activeDirectoryName.toLowerCase().startsWith(dir)),
      );
      return { code: 200, status: "Success", users, count: users.length };
    },
  },
  {
    pattern: "/ad/getallgroupsbydomain",
    respond: (body = {}) => {
      const q = String(body?.filter || "").toLowerCase();
      const groups = DIRECTORY_GROUPS.filter(
        (g) => (!body?.adId || g.adId === Number(body.adId)) && g.groupName.toLowerCase().includes(q),
      ).map(({ id, groupName }) => ({ id, groupName }));
      return { code: 200, status: "Success", groups, count: groups.length };
    },
  },
];
